import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CenterFrameLayout } from 'Layouts';
import { SecondaryText } from 'Components/Texts';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import {
  CL_PREAUTH_GIFTING_PARAM,
  CL_PREAUTH_PRE_SELECTED_PLAN_PARAM,
  CL_PREAUTH_ROUTER_STATE_PARAM,
  CL_PREAUTH_URL_SEARCH_PARAM,
  FINISH_VERIFYING_SHEER_EMAIL_ID,
  GIFTING_QUERY_PARAM_NAME,
  GIFTING_QUERY_PARAM,
  UNSUCCESSFUL_SHEER_VERIFY_EMAIL_ID,
  VA_QUERY_PARAM,
  CENTRAL_AUTH_GIFTING_VARIANT,
} from 'Utils/Constants';
import { checkIfShowSheerBanner, isDefaultPlanFlow } from 'Utils/Analytics/Amplitude';
import { SheerIdBanner } from 'Features/SheerIdPromoBanner/SheerIdBanner';
import { getSheerProgramFromSKU } from 'Utils/SheerIdHelpers';
import { encodeSearchParams } from 'Utils/Path';
import { createUserAccount, handleSignInSuccess, handleSignUpSuccess } from 'Utils/SignInHelpers';
import CreateAccountForm from 'Forms/CreateAccountForm';
import { getPlans } from 'State/Plans';
import { useStore } from 'Hooks/useStore';
import './CreateContainer.css';
import { callCentralAuthLogin } from 'Utils/CentralizedLoginSDK';
import { useAddPreAuthParams } from 'Hooks/useAddPreAuthParams';
import { Loader } from 'Components/Loader';
import { signOut } from 'State/User';

export const CreateContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { hasSubscription, isFetching, isStatusSuccess, isSuccess, isIAPUser, hasActiveSubscription } =
    useStore('subscription');

  const { routes, defaultConfig } = useStore('appConfig');
  const user = useStore('user');
  const { bango, sheerId } = useStore('integrations');
  const pageStartSource = useStore('tracking.pageStartSource');
  const { requirePlanSelection, isGifting: isGiftingPurchaseMode } = useStore('purchaseMode');
  const { hasShippingAddress } = useStore('checkout');
  const { isPlanSelected, selectedPlan, selectPlanActionComplete } = useStore('plans');
  const { secondScreen } = useStore('secondScreen');

  const { verificationId } = sheerId;
  const isSheerIdVerification = (location?.state?.from?.pathname || '').includes(routes.sheerIdVerification);
  const isBango = bango.subscriptionId;

  //This is used so the redirects don't trigger after account creation on this page
  const isAuthenticatedOnFirstRender = useRef(user.isAuthenticated);

  const [checkedForPreselectedPlan, setCheckedForPreselectedPlan] = useState(false);

  const isLoading =
    (isFetching && !isStatusSuccess) || (requirePlanSelection && !selectPlanActionComplete && !isPlanSelected);

  const urlParams = new URLSearchParams(location.search);

  const routeParams = useParams();
  const preSelectedPlan = routeParams.preSelectedPlan ?? urlParams.get('preSelectedPlan') ?? '';

  const bangoProductId = bango.productId;
  const sheerProgramType = urlParams.get('sheerVerification') || getSheerProgramFromSKU(bangoProductId);
  const isVeteransAdv = urlParams.get(VA_QUERY_PARAM);

  const brazeDefaultParam = urlParams.get('lid');
  const emailCampaignId = urlParams.get('cmpid');
  const fromMilitaryEmailDeeplink =
    !!(brazeDefaultParam && emailCampaignId) &&
    [FINISH_VERIFYING_SHEER_EMAIL_ID, UNSUCCESSFUL_SHEER_VERIFY_EMAIL_ID].some((id) => {
      return emailCampaignId.includes(id);
    });

  const addPreAuthParams = useAddPreAuthParams();

  let searchParam;
  if (isGiftingPurchaseMode) {
    searchParam = `${GIFTING_QUERY_PARAM}`;
  } else if (isVeteransAdv) {
    searchParam = `${VA_QUERY_PARAM}=${isVeteransAdv}`;
  } else {
    const searchParamObj = new URLSearchParams();
    searchParamObj.set('return_to', `${location.pathname}${location.search}`); //create account screen including params
    searchParam = searchParamObj.toString();
  }

  const isGifting = isGiftingPurchaseMode || urlParams.get(GIFTING_QUERY_PARAM_NAME) === 'true';

  useEffect(() => {
    if (user.isAuthenticated) {
      signOut(false, { redirectUri: window.location.href });

      return;
    }

    // make sure that if there's a preselected plan that `getPlans()` is fully run first
    // so that the plan is set in the store
    if ((checkedForPreselectedPlan || preSelectedPlan.trim().length < 1) && !isLoading) {
      if (!user.isAuthenticated) {
        trackEvent(TRACKING_ENUMS.EVENT_SIGNUP_STARTED);

        const urlParamsToStore = new URLSearchParams(urlParams);
        // centralized auth route
        // store the current url params before redirecting to centralized login so we can retrieve them later
        const stateUuid = addPreAuthParams({
          ...(urlParamsToStore ? { [CL_PREAUTH_URL_SEARCH_PARAM]: Object.fromEntries(urlParamsToStore) } : {}),
          ...(selectedPlan ? { [CL_PREAUTH_PRE_SELECTED_PLAN_PARAM]: selectedPlan.planId } : {}),
          ...(isGifting ? { [CL_PREAUTH_GIFTING_PARAM]: true } : {}),
          ...(location.state
            ? (() => {
                try {
                  return { [CL_PREAUTH_ROUTER_STATE_PARAM]: JSON.stringify(location.state) };
                } catch (e) {
                  console.warn('Error trying to serialize location.state', e);
                  return {};
                }
              })()
            : {}),
        });

        // call centralized login which if successful will redirect the user to /callback
        // fall back to url param in case user visits this page directly such that purchaseMode has not yet been set
        void callCentralAuthLogin({
          state: stateUuid,
          isSignUp: true,
          ...(isGifting ? { variant: CENTRAL_AUTH_GIFTING_VARIANT } : {}),
        });
      } else if (isAuthenticatedOnFirstRender.current && isSuccess) {
        // redirect logic for already logged in user when component first renders
        // if both subscription status (isStatusSuccess) and subscription data (isSuccess and hasSubscription)
        // are loaded
        //    if subscription active, redirect to either "already currently subscribed" page
        //    (to military variant of page if isIAPUser)
        //    otherwise redirect to change plans page
        if (isStatusSuccess && hasSubscription && !isBango) {
          if (verificationId) {
            if (isIAPUser && hasActiveSubscription) {
              navigate(`${routes.signupCurrentSubscriberMilitaryFirst}${location.search}`, { replace: true });
              return;
            }
            const param = preSelectedPlan ? '/' + preSelectedPlan : '';
            navigate(`${routes.changePlans + param}${location.search}`, { replace: true });
            return;
          } else if (!sheerProgramType) {
            navigate(`${routes.signupCurrentSubscriber}${location.search}`, { replace: true });
            return;
          }
        }
        // redirection logic for bango/sheer if user does not have subscription
        if (selectPlanActionComplete) {
          if (bango.isSiriusXM) {
            navigate(routes.siriusXMBango, { replace: true });
            return;
          } else if (bango.isVerizon) {
            navigate(routes.verizonBango, { replace: true });
            return;
          } else if (isPlanSelected) {
            navigate(routes.payment, { replace: true });
            return;
          } else if (!bango.subscriptionId && !sheerProgramType) {
            navigate(routes.signupPlans, { replace: true });
            return;
          } else if (sheerProgramType) {
            navigate(`${routes.sheerIdVerification}?sheerVerification=${sheerProgramType}`, { replace: true });
            return;
          }
        }
      }

      if (selectedPlan?.hasMerchandise && !hasShippingAddress) {
        navigate(routes.shipping, { replace: true });
        return;
      }

      // if from email deeplink, we want to directly take the user to sign in instead of create account
      if (fromMilitaryEmailDeeplink && sheerProgramType) {
        const searchParams = encodeSearchParams({ return_to: `${routes.sheerIdVerification}${location.search}` });
        const url = `${routes.signin}?${searchParams}`;

        navigate(url, { replace: true });
        return;
      }

      // if not logged in and if no plan selected (except for bango/sheer/veterans adv users), redirect to select plan
      if (
        requirePlanSelection &&
        selectPlanActionComplete &&
        !isPlanSelected &&
        preSelectedPlan.trim().length < 1 &&
        checkedForPreselectedPlan &&
        !isBango &&
        !isSheerIdVerification &&
        !isVeteransAdv
      ) {
        navigate(routes.signupPlans, { replace: true });
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, user.isAuthenticated, isSuccess, isPlanSelected, checkedForPreselectedPlan]);

  useEffect(() => {
    async function fetchPlansForPreSelectedPlan() {
      if (preSelectedPlan.trim().length > 0) {
        //Needed since sometimes other pages redirect to this page with a preSelectedPlan added in url
        if (!fromMilitaryEmailDeeplink) {
          await getPlans(preSelectedPlan);
        }

        setCheckedForPreselectedPlan(true);
      }
    }

    fetchPlansForPreSelectedPlan();
  }, [preSelectedPlan, fromMilitaryEmailDeeplink]);

  let title = 'Checking your subscription';
  if (!isLoading) {
    title = isGifting ? 'Create Your Account To Redeem' : 'Create Your Account';
  }

  const { enableSheerIdBanner } = defaultConfig;
  const showBanner = (isDefaultPlanFlow() && !isBango && !isGifting && enableSheerIdBanner) || checkIfShowSheerBanner();

  const onSignInSuccess = handleSignInSuccess(location.pathname);
  const onSignUpSuccess = handleSignUpSuccess(isSheerIdVerification, isVeteransAdv, urlParams, (path) => {
    navigate(path);
  });

  if (!secondScreen) {
    return <Loader />;
  }

  return (
    <CenterFrameLayout
      title={title}
      showLoader={isLoading}
      modernLayout
      customFrameClass={showBanner && 'frameWithSheerIdBanner'}
    >
      {showBanner && <SheerIdBanner />}
      <CreateAccountForm
        submitButtonText="Continue"
        pageStartSource={pageStartSource}
        onFormSubmit={(values) => createUserAccount(values, onSignInSuccess, onSignUpSuccess)}
      />
      <SecondaryText
        key="b"
        alignText="centerText"
        text={[
          'Already have an account? ',
          <Link
            key="0"
            to={{
              pathname: routes.signin,
              search: searchParam,
            }}
            className="blueLink"
          >
            Sign in
          </Link>,
        ]}
      />
    </CenterFrameLayout>
  );
};
